import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';
import { noTransform } from '../../layout/keyframes';
import { ButtonReset, inputPlaceholderVisuallyHidden } from '../../layout/reset';
import type { InputTextStateTypeProps } from '.';

export const InputWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
`;

export const Label = styled.label`
  position: absolute;
  top: 50%;
  left: var(--gutter-inline);
  right: var(--gutter-inline);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--label-color-override, var(--c-input-label));
  line-height: var(--label-line-height);
  transform: translateY(var(--label-translate-y));
  transform-origin: center left;
  transition: transform 200ms ease;
  pointer-events: none;
`;

export const ButtonWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: var(--gutter-block);
  padding-bottom: var(--gutter-block);
  padding-right: var(--gutter-block);
`;

const Affix = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  max-width: 50%;
  padding-left: var(--gutter-inline);
  color: var(--c-input-affix);
`;

export const Prefix = styled(Affix)`
  padding-left: var(--gutter-inline);
`;

export const Suffix = styled(Affix)`
  padding-right: var(--gutter-inline);
`;

export const Truncate = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Unit = styled.div<{ before?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: ${(props) => (props.before ? 'var(--gutter-inline)' : '0')};
  padding-right: ${(props) => (props.before ? '0' : 'var(--gutter-inline)')};
  order: ${(props) => (props.before ? -1 : 0)};
  color: var(--c-input-affix);
`;

interface AffixSelectProps {
  $type?: 'prefix' | 'suffix';
}

export const AffixSelect = styled.select<AffixSelectProps>`
  --icon-size: ${size(2.5)};
  --icon-spacing: ${size(0.25)};
  --padding-inline: var(--gutter-inline);

  flex-shrink: 0;
  display: block;
  height: ${(props) => props.theme.distances.inputHeight};
  appearance: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  border: 0;
  text-indent: 0.01px;
  padding: 0;
  background-color: transparent;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="M12 15 7 9h10l-5 6Z" style="fill: var(--c-input-text);" /></svg>');
  background-repeat: no-repeat;
  background-size: var(--icon-size);
  border-radius: 0;
  color: var(--c-input-affix);
  font-size: var(--font-size);
  cursor: pointer;

  &:focus {
    outline: none;
    color: var(--c-focus);
  }

  ::-ms-expand {
    display: none;
  }

  ::-moz-focus-inner {
    border: 0;
  }

  &[disabled] {
    opacity: 0.37;
    cursor: not-allowed;
  }

  ${({ $type }) =>
    $type === 'prefix'
      ? css`
          padding-left: var(--padding-inline);
          padding-right: calc(var(--icon-size) + var(--icon-spacing));
          background-position: 100% 50%;
        `
      : $type === 'suffix'
        ? css`
            padding-right: calc(var(--padding-inline) + var(--icon-size) + var(--icon-spacing));
            background-position: calc(100% - var(--padding-inline)) 50%;
          `
        : undefined}
`;

export const AffixSelectOption = styled.option`
  color: var(--c-text-main);
`;

export const Input = styled.input`
  ${inputPlaceholderVisuallyHidden};
  display: block;
  width: 100%;
  height: ${(props) => props.theme.distances.inputHeight};
  padding-top: calc(
    ((var(--label-active-font-size) * var(--label-line-height)) * 1px) + var(--gutter-block) +
      var(--label-offset-inline-end)
  );
  padding-bottom: var(--gutter-block);
  padding-left: var(--gutter-inline);
  padding-right: var(--gutter-inline);
  background-color: transparent;
  font-size: var(--font-size);
  border: none;
  border-radius: 0;
  box-shadow: none;
  appearance: none;

  &:not(:placeholder-shown),
  &:-webkit-autofill,
  &:focus {
    & ~ ${Label} {
      --label-translate-y-pushup: calc(
        (
            (
                (var(--height) - ((var(--label-active-font-size) * var(--label-line-height)) * 1px)) /
                  2
              ) * -1
          ) + var(--gutter-block)
      );

      transform: translateY(var(--label-translate-y)) translateY(var(--label-translate-y-pushup))
        scale(calc(var(--label-active-font-size) / var(--label-font-size)));
    }
  }

  &:focus {
    outline: none;
  }

  &[disabled] {
    cursor: not-allowed;
  }
`;

export const StateWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  max-width: 50%;
  padding-inline: var(--gutter-inline);
`;

interface StateProps {
  $tone?: InputTextStateTypeProps;
}

export const State = styled.div<StateProps>`
  --_state-size: ${size(3)};

  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--_state-size);
  height: var(--_state-size);
  border-radius: 50%;
  background-color: var(--_state-bg, var(--c-semantic-neutral-300));
  color: var(--_state-fg, var(--c-semantic-neutral-700));
  transform: scale(0);
  animation: ${noTransform} 300ms cubic-bezier(0.47, 1.64, 0.41, 0.8) forwards;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          --_state-bg: var(--c-semantic-success-500);
          --_state-fg: var(--c-semantic-neutral-100);
        `
      : $tone === 'warning'
        ? css`
            --_state-bg: var(--c-semantic-warning-500);
            --_state-fg: var(--c-semantic-neutral-100);
          `
        : $tone === 'error'
          ? css`
            --_state-bg: var(--c-semantic-error-500);
            --_state-fg: var(--c-semantic-neutral-100);
          `
          : $tone === 'info'
            ? css`
            --_state-bg: var(--c-semantic-info-500);
            --_state-fg: var(--c-semantic-neutral-100);
          `
            : undefined}
`;

interface StateMessageProps {
  $tone?: InputTextStateTypeProps;
}

export const StateMessage = styled.div<StateMessageProps>`
  margin-top: ${size(0.5)};
  font-size: ${(props) => props.theme.font.size.xsmall};
  line-height: ${(props) => props.theme.font.lineHeight.main};
  color: var(--_input-state-message-color, --c-text-muted);
  font-weight: var(--font-weight-medium);

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          --_input-state-message-color: var(--c-semantic-success-500);
        `
      : $tone === 'warning'
        ? css`
            --_input-state-message-color: var(--c-semantic-warning-700);
          `
        : $tone === 'error'
          ? css`
            --_input-state-message-color: var(--c-semantic-error-500);
          `
          : $tone === 'info'
            ? css`
            --_input-state-message-color: var(--c-semantic-info-500);
            `
            : undefined}
`;

export const Button = styled(ButtonReset)`
  position: relative;
  display: block;
  height: 100%;
  padding: 0 ${size(2)};
  border-radius: var(--border-radius);
  background-color: var(--c-primary-bg);
  color: var(--c-primary-fg);
  font-size: ${(props) => props.theme.font.size.xsmall};
  font-weight: var(--font-weight-bold);
  transform-origin: 50% 50%;
  transition: transform 300ms cubic-bezier(0.47, 1.64, 0.41, 0.8);

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    background-color: rgba(var(--c-overlay-rgb), 1);
    border-radius: inherit;
    mix-blend-mode: soft-light;
    pointer-events: none;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  &:enabled {
    &:active {
      transform: scale(0.97);
    }

    &:hover {
      :before {
        opacity: 1;
      }
    }
  }

  &[disabled] {
    opacity: 0.37;
    cursor: default;

    &::before {
      content: none;
    }
  }
`;

export const ColorPicker = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-left: var(--gutter-inline);

  & + ${Input} {
    padding-left: ${size(1)};
  }
`;

export const ColorPreview = styled.div`
  position: relative;
  width: ${size(2)};
  height: ${size(2)};
  border-radius: var(--border-radius);
  border: 1px solid var(--c-border-main);
  color: transparent;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
      45deg,
      transparent,
      transparent 45%,
      var(--c-input-error-border) 45%,
      var(--c-input-error-border) 55%,
      transparent 55%,
      transparent 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: currentColor;
    z-index: 1;
  }
`;

interface WrapperProps {
  $error?: boolean;
  $disabled?: boolean;
}

export const Wrapper = styled.div<WrapperProps>`
  --height: ${(props) => props.theme.distances.inputHeight};
  --gutter-inline: ${size(1.5)};
  --gutter-block: ${size(1)};
  --border-color: var(--c-input-border);
  --font-size: 16; // Unitless value representing px
  --label-font-size: var(--font-size); // Unitless value representing px
  --label-active-font-size: 12; // Unitless value representing px
  --label-line-height: 1.2; // Required to prevent text clipping
  --label-offset-inline-end: ${size(0.25)};
  --label-translate-y: -50%;

  position: relative;
  width: 100%;
  display: flex;
  align-items: stretch;
  background-color: var(--c-input-bg);
  color: var(--c-input-text);
  border-bottom: 1px solid var(--border-color-override, var(--border-color));
  border-top-left-radius: var(--border-radius);
  border-top-right-radius: var(--border-radius);

  &:focus-within {
    --border-color: var(--c-input-border-active);
  }

  ${({ $error }) =>
    $error &&
    css`
      --border-color-override: var(--c-input-error-border);
      --label-color-override: var(--c-input-error-text);
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.37;
      cursor: not-allowed;
    `}
`;
